import { useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

// Fullscreen photo viewer. Scrim click or Escape closes, arrow keys and the
// circular buttons move between photos. Parent owns the index.
export default function PhotoLightbox({ photos = [], index = 0, onClose, onIndex }) {
  const count = photos.length;

  const prev = useCallback(() => {
    if (count > 1) onIndex((index - 1 + count) % count);
  }, [count, index, onIndex]);

  const next = useCallback(() => {
    if (count > 1) onIndex((index + 1) % count);
  }, [count, index, onIndex]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
      else if (e.key === "ArrowRight") { e.preventDefault(); next(); }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, prev, next]);

  // Lock page scroll while the lightbox is open.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  return (
    <motion.div
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.18 }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90"
      onClick={onClose}
    >
      {/* Counter — quiet white text, top center */}
      <span className="pointer-events-none absolute top-5 left-1/2 -translate-x-1/2 text-sm font-medium text-white/70">
        {index + 1} of {count}
      </span>

      {/* Close */}
      <button
        type="button"
        aria-label="Close photo viewer"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-white transition hover:bg-white/30"
      >
        <X size={20} />
      </button>

      {/* Prev / next — hidden when there is only one photo */}
      {count > 1 && (
        <>
          <button
            type="button"
            aria-label="Previous photo"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white text-brand-ink shadow-ambient transition hover:scale-105 sm:left-6"
          >
            <ChevronLeft size={22} />
          </button>
          <button
            type="button"
            aria-label="Next photo"
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white text-brand-ink shadow-ambient transition hover:scale-105 sm:right-6"
          >
            <ChevronRight size={22} />
          </button>
        </>
      )}

      <motion.img
        key={index}
        src={photos[index]}
        alt={`Photo ${index + 1} of ${count}`}
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[88vh] max-w-[92vw] select-none rounded-lg object-contain"
        draggable={false}
      />
    </motion.div>
  );
}
